<template>
  <v-form
    ref="formRef"
    class="multi-col-validation"
    @submit.prevent="handleSubmitBtnClick"
  >
    <v-row>
      <v-col
        md="3"
        cols="6"
      >
        <date-picker
          :date-iso.sync="dataInicial"
          label="Data início"
          hide-details
          required
          :min-date-iso="dataInicialMinima"
        />
      </v-col>

      <v-col
        md="3"
        cols="6"
      >
        <date-picker
          :date-iso.sync="dataFinal"
          label="Data fim"
          hide-details
          required
          :min-date-iso="dataFinalMinima"
        />
      </v-col>

      <v-col
        md="3"
        cols="6"
      >
        <time-picker
          :time-iso.sync="horarioInicial"
          label="Horário início"
          hide-details
          required
          :min-time-iso="horarioInicialMinimo"
        />
      </v-col>

      <v-col
        md="3"
        cols="6"
      >
        <time-picker
          :time-iso.sync="horarioFinal"
          label="Horário fim"
          hide-details
          required
          :min-time-iso="horarioFinalMinimo"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col
        md="3"
        cols="6"
      >
        <v-select
          v-model="agenteIndisponibilidadeMotivoId"
          dense
          outlined
          clearable
          validate-on-blur
          label="Motivo"
          item-value="id"
          item-text="nome"
          :items="motivosIndisponibilidades"
          :loading="isGetMotivosIndisponibilidadesLoading"
          :rules="[validators.select]"
        />
      </v-col>

      <v-slide-x-transition>
        <v-col
          v-if="selecionouOutroMotivo"
          md="3"
          cols="6"
        >
          <v-text-field
            v-model="outroMotivoObservacao"
            dense
            outlined
            clearable
            validate-on-blur
            label="Outro motivo"
            hide-details
            :rules="[validators.required]"
          />
        </v-col>
      </v-slide-x-transition>

      <v-col
        :md="selecionouOutroMotivo ? 6 : 9"
        cols="12"
        class="d-flex justify-end"
      >
        <v-btn
          color="primary"
          type="submit"
          :loading="isSalvarIndisponibilidadeLoading"
        >
          {{ submitBtnTitle }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import Vue from 'vue'
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { required, select } from '@/@core/utils/validation'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import TimePicker from '@/components/time-picker/TimePicker.vue'
import IndisponibilidadeMotivoEnum from '@/models/entities/agente/IndisponibilidadeMotivoEnum'
import AgenteService from '@/services/modules/AgenteService'
import useForm from '@/composables/useForm'
import useUserStore from '@/store/user'
import { concatenarIsoDateStringETimeStringEmIsoDateTimeString, getDataAtualNoFormatoIsoDateString, getHorarioLocalAtualComApenasHorasEMinutos } from '@/utils/dateTime'

export default {
  emits: ['refetch-data'],

  components: {
    DatePicker,
    TimePicker,
  },

  props: {
    indisponibilidadeSelecionada: {
      type: Object,
      default: null,
    },
  },

  setup(props, { emit }) {
    const { id: agenteId } = storeToRefs(useUserStore())
    const { formRef, loading: isSalvarIndisponibilidadeLoading, resetForm, validateForm, FORM_ERROR } = useForm()
    const isNovaIndisponibilidade = computed(() => !props.indisponibilidadeSelecionada)
    const submitBtnTitle = computed(() => (isNovaIndisponibilidade.value ? 'Criar' : 'Salvar'))

    const dataInicial = ref(null)
    const dataFinal = ref(null)
    const horarioInicial = ref(null)
    const horarioFinal = ref(null)

    const dataInicialMinima = ref(getDataAtualNoFormatoIsoDateString())
    const dataFinalMinima = computed(() => (dataInicial.value ? dataInicial.value : getDataAtualNoFormatoIsoDateString()))

    const horarioInicialMinimo = ref(props.getTimeStringComApenasHorasEMinutos(new Date().toLocaleTimeString()))
    const horarioFinalMinimo = computed(() => (horarioInicial.value ? horarioInicial.value : getHorarioLocalAtualComApenasHorasEMinutos()))

    const agenteIndisponibilidadeMotivoId = ref(null)
    const outroMotivoObservacao = ref(null)
    const motivosIndisponibilidades = ref([])
    const isGetMotivosIndisponibilidadesLoading = ref(false)

    const selecionouOutroMotivo = computed(() => agenteIndisponibilidadeMotivoId.value === IndisponibilidadeMotivoEnum.Outro)

    watch(
      dataInicial,
      v => {
        if (v > dataFinal.value) {
          dataFinal.value = null
        }
      },
    )

    watch(
      horarioInicial,
      v => {
        if (v > horarioFinal.value) {
          horarioFinal.value = null
        }
      },
    )

    watch(
      () => props.indisponibilidadeSelecionada,
      v => {
        if (!v) {
          dataInicial.value = null
          dataFinal.value = null
          horarioInicial.value = null
          horarioFinal.value = null
          agenteIndisponibilidadeMotivoId.value = null
          outroMotivoObservacao.value = null

          return
        }

        const [dataInicial2, horarioInicial2] = v.periodoInicial.split('T')
        const [dataFinal2, horarioFinal2] = v.periodoFinal.split('T')

        dataInicial.value = dataInicial2
        dataFinal.value = dataFinal2
        horarioInicial.value = props.getTimeStringComApenasHorasEMinutos(horarioInicial2)
        horarioFinal.value = props.getTimeStringComApenasHorasEMinutos(horarioFinal2)
        agenteIndisponibilidadeMotivoId.value = v.agenteIndisponibilidadeMotivoId
        outroMotivoObservacao.value = v.outroMotivoObservacao
      },
      {
        deep: true,
        immediate: true,
      },
    )

    async function getMotivosIndisponibilidades() {
      if (isGetMotivosIndisponibilidadesLoading.value) return

      try {
        isGetMotivosIndisponibilidadesLoading.value = true
        motivosIndisponibilidades.value = await AgenteService.getMotivosIndisponibilidades()
      } finally {
        isGetMotivosIndisponibilidadesLoading.value = false
      }
    }

    onMounted(getMotivosIndisponibilidades)

    async function handleSubmitBtnClick() {
      if (isSalvarIndisponibilidadeLoading.value) return

      if (!validateForm()) {
        Vue.$toast(FORM_ERROR)
        return
      }

      try {
        isSalvarIndisponibilidadeLoading.value = true

        if (isNovaIndisponibilidade.value) {
          await AgenteService.criarIndisponibilidadeAgente(agenteId.value, {
            periodoInicial: concatenarIsoDateStringETimeStringEmIsoDateTimeString(dataInicial.value, horarioInicial.value),
            periodoFinal: concatenarIsoDateStringETimeStringEmIsoDateTimeString(dataFinal.value, horarioFinal.value),
            agenteIndisponibilidadeMotivoId: agenteIndisponibilidadeMotivoId.value,
            outroMotivoObservacao: outroMotivoObservacao.value,
          })
        } else {
          await AgenteService.atualizarIndisponibilidadeAgente(agenteId.value, props.indisponibilidadeSelecionada.id, {
            periodoInicial: concatenarIsoDateStringETimeStringEmIsoDateTimeString(dataInicial.value, horarioInicial.value),
            periodoFinal: concatenarIsoDateStringETimeStringEmIsoDateTimeString(dataFinal.value, horarioFinal.value),
            agenteIndisponibilidadeMotivoId: agenteIndisponibilidadeMotivoId.value,
            outroMotivoObservacao: outroMotivoObservacao.value,
          })
        }

        resetForm()
        emit('refetch-data')
      } finally {
        isSalvarIndisponibilidadeLoading.value = false
      }
    }

    return {
      formRef,
      isSalvarIndisponibilidadeLoading,
      submitBtnTitle,
      dataInicial,
      dataFinal,
      horarioInicial,
      horarioFinal,
      dataInicialMinima,
      dataFinalMinima,
      horarioInicialMinimo,
      horarioFinalMinimo,
      agenteIndisponibilidadeMotivoId,
      outroMotivoObservacao,
      motivosIndisponibilidades,
      isGetMotivosIndisponibilidadesLoading,
      selecionouOutroMotivo,
      handleSubmitBtnClick,
      validators: {
        select,
        required,
      },
    }
  },
}
</script>
